/**
 * Recovery poller for the maintenance state.
 *
 * While the app is in 'maintenance' state, this module re-runs the full
 * discriminator probe every 30 seconds via `maybeProbe('poller')`. The
 * discriminator consults /favicon.ico (origin reachability), /api/maintenance
 * (operator-driven flag from the Pages Function), and /health (backend
 * signal). This keeps the takeover honest: dismissing only when the operator
 * clears MAINTENANCE_MODE AND the backend confirms it is back.
 *
 * The poller is self-skipping: each tick checks `getState().status` and
 * skips the probe if state is no longer 'maintenance'. The interval itself
 * is cleared by the state subscription in `index.ts` when status changes;
 * the per-tick guard is belt-and-suspenders for the brief window where a
 * tick could fire between the state flip and the subscription's stopPoller
 * call.
 *
 * Network errors and non-recovery responses are silent: we already KNOW
 * we're in maintenance; the probe just decides whether to flip out.
 *
 * `'poller'` reason is debounce-bypassing in maybeProbe (alongside
 * `'manual'`). The 30-second interval dominates the 10-second debounce
 * window, so each tick must run a fresh probe rather than reuse a recent
 * debounced result. Sentry breadcrumbs distinguish poller ticks from
 * operator-driven clicks via the reason field. `maybeProbe`'s in-flight
 * coalescing still protects against poller-tick + detector-trigger races.
 */
import { maybeProbe } from './detector';
import { getState } from './state';

// 30 seconds is intentional and load-bearing for cost: it aligns with the
// Pages Function's `Cache-Control: max-age=30` so each poll tick lands on
// the cached `/api/maintenance` response window, making it a cheap cache
// HIT instead of a Function invocation. Bumping this value asymmetrically
// against the Pages Function cache directive would multiply edge-function
// invocations during real outages where many clients are stuck in the
// maintenance state simultaneously.
const POLL_INTERVAL_MS = 30_000;

let intervalId: ReturnType<typeof setInterval> | null = null;

async function pollOnce(): Promise<void> {
  if (getState().status !== 'maintenance') return;
  await maybeProbe('poller');
}

/**
 * Start polling every 30s. Idempotent: calling twice does not create a
 * second interval.
 */
export function startPoller(): void {
  if (intervalId !== null) return;
  intervalId = setInterval(() => {
    void pollOnce();
  }, POLL_INTERVAL_MS);
}

/**
 * Stop the poll interval. Idempotent: safe to call when no interval is
 * running.
 */
export function stopPoller(): void {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}
